import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { NumberRangeScope } from '@models/enums/NumberRangeScope.ts';

/**
 * Hook for translating number range scope values
 * @returns Functions for number range scope translation
 *
 * @category Hooks
 * @group Translations
 */
export const useNumberRangeScopeTranslation = () => {
  const { t } = useTranslation();

  /**
   * Translates a number range scope to its localized name
   * @param scope The scope value to translate
   * @returns The translated scope name
   */
  const translateScope = useCallback(
    (scope: string | NumberRangeScope): string => {
      // Convert to string and ensure first letter is lowercase while preserving camelCase
      const scopeKey = String(scope).charAt(0).toLowerCase() + String(scope).slice(1);
      if (scopeKey === '' || scopeKey === 'undefined') {
        return '';
      }
      return t(`components.shared.numberRanges.scope.${scopeKey}`);
    },
    [t]
  );

  /**
   * Creates a value getter function for AG-Grid that translates number range scope values
   * @param field The field name in the data that contains the scope value
   * @returns A value getter function for AG-Grid
   */
  const getScopeValueGetter = useCallback(
    (field: string = 'scope') => {
      return <T extends Record<string, unknown>>(params: { data: T }) => {
        return params.data?.[field] ? translateScope(params.data[field] as string | NumberRangeScope) : '';
      };
    },
    [translateScope]
  );

  const { scopeDisplayMap, scopeReverseMap } = useMemo(() => {
    const displayMap = new Map<string, string>();
    const reverseMap = new Map<string, string>();

    Object.values(NumberRangeScope).forEach(scope => {
      const translated = translateScope(scope);
      displayMap.set(scope, translated);
      reverseMap.set(translated, scope);
    });

    return {
      scopeDisplayMap: displayMap,
      scopeReverseMap: reverseMap
    };
  }, [translateScope]);

  return {
    translateScope,
    getScopeValueGetter,
    scopeDisplayMap,
    scopeReverseMap
  };
};
